/**
 * Note Pipeline Polling Hook
 *
 * Uses TanStack Query to poll note status for pipeline visualization.
 * Polling stops when note reaches terminal state (COMPLETED or FAILED).
 */

import { useQuery } from '@tanstack/react-query';
import { getNote } from '../services/notes';
import { usePipelineState } from './usePipelineState';
import type { NoteApiResponse } from '../types/api';

/** Polling interval in milliseconds */
const POLLING_INTERVAL = 3000; // 3 seconds

/**
 * Check if note is in a terminal state (no more processing).
 */
function isTerminalState(note: NoteApiResponse | undefined): boolean {
  if (!note) return false;
  // REVIEW is a pause state — stop polling because user must act
  return note.status === 'COMPLETED' || note.status === 'FAILED' || note.status === 'REVIEW';
}

/**
 * Custom hook for fetching note data with automatic polling.
 *
 * Features:
 * - Polls every 3 seconds while note is PENDING or PROCESSING
 * - Stops polling when note reaches COMPLETED or FAILED
 * - Returns both raw note data and derived pipeline state
 *
 * @param noteId - UUID of the note to monitor
 * @returns Query result with note data and pipeline state
 */
export function useNotePipeline(noteId: string | undefined) {
  const query = useQuery({
    queryKey: ['note', noteId],
    queryFn: () => getNote(noteId!),
    enabled: !!noteId,
    // Poll while processing, stop at terminal state
    refetchInterval: (query) => {
      const note = query.state.data;
      if (isTerminalState(note)) {
        return false; // Stop polling
      }
      return POLLING_INTERVAL;
    },
    // Reduce stale time for responsive updates
    staleTime: POLLING_INTERVAL - 1000,
  });

  // Derive pipeline state from note data
  const pipelineState = usePipelineState(query.data);

  return {
    ...query,
    pipelineState,
    isProcessing: query.data?.status === 'PROCESSING',
    isReviewing: query.data?.status === 'REVIEW',
    isCompleted: query.data?.status === 'COMPLETED',
    isFailed: query.data?.status === 'FAILED',
  };
}
